// atlas/apps/web/src/components/WorkRequestModal.tsx
// PM/admin-gated "Create work request" modal — files a new task via
// POST /api/tasks. Renders nothing for viewer/member roles.
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { pbAuthedFetch } from '../authHeaders';

interface MeResponse {
  id: number;
  email: string;
  name: string;
  role: string;
  isAdmin: boolean;
}

interface WorkRequestModalProps {
  onClose: () => void;
}

export function WorkRequestModal({ onClose }: WorkRequestModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');

  const { data: me, isLoading } = useQuery<MeResponse>({
    queryKey: ['me'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/me');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const createTask = useMutation({
    mutationFn: async () => {
      const res = await pbAuthedFetch('/api/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, description, priority, source: 'work-request' }),
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    onSuccess: onClose,
  });

  if (isLoading) return null;
  const canSubmitWorkRequest = !!me && (me.isAdmin || me.role === 'pm');
  if (!canSubmitWorkRequest) return null;

  return (
    <div className="mo-overlay" onClick={onClose}>
      <div className="mo-card" onClick={(e) => e.stopPropagation()}>
        <div className="mo-head">
          <div className="mo-title">New Work Request</div>
          <button className="mo-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="mo-body">
          <div className="ib-form">
            <label className="ib-field">
              <span className="ib-label">Title</span>
              <input className="ib-input" value={title} onChange={(e) => setTitle(e.target.value)} />
            </label>
            <label className="ib-field">
              <span className="ib-label">Description</span>
              <textarea className="ib-textarea" rows={5} value={description} onChange={(e) => setDescription(e.target.value)} />
            </label>
            <label className="ib-field">
              <span className="ib-label">Priority</span>
              <select className="ib-input" value={priority} onChange={(e) => setPriority(e.target.value)}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </label>
          </div>
        </div>
        <div className="mo-foot">
          <button className="btn btn--sm" onClick={onClose}>Cancel</button>
          <button
            className="btn btn--primary btn--sm"
            disabled={!title.trim() || createTask.isPending}
            onClick={() => createTask.mutate()}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}
